import { useCallback, useRef } from "react";
import { Ramp } from "../../../../../../types";
import { useReferenceAircraftState } from "../../stores/useReferenceAircraftState";
import { adjustSquare, getCenter, snapSquare } from "../math";
import { DraggingPoint, DraggingShape, Shape } from "../RampCanvas";
import { updateAtId } from "../utils";

type Props = {
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  currentShape: Shape;
  setDraggingPoint: React.Dispatch<React.SetStateAction<DraggingPoint>>;
  setDraggingShape: React.Dispatch<React.SetStateAction<DraggingShape>>;
  draggingPoint: DraggingPoint;
  draggingShape: DraggingShape;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  ramp: Ramp;
  isShiftPressed: boolean;
  setMousePosition: React.Dispatch<
    React.SetStateAction<{ x: number; y: number }>
  >;
  getActiveShapes: () => Shape[];
  pixelsToFeetRatio: number;
};

export const useOnMouseMove = (props: Props) => {
  const {
    selectedTool,
    shapes,
    setShapes,
    setDraggingShape,
    draggingPoint,
    draggingShape,
    canvasRef,
    isShiftPressed,
    setMousePosition,
    getActiveShapes,
    pixelsToFeetRatio,
  } = props;

  const {
    isDragging: isReferenceAircraftDragging,
    position: referencePosition,
    setPosition: setReferencePosition,
  } = useReferenceAircraftState();

  // Track last mouse position for drag delta calculation
  const lastMousePos = useRef<{ x: number; y: number } | null>(null);

  return useCallback(
    (event: React.MouseEvent) => {
      if (selectedTool === "Measuring") {
        return;
      }

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      setMousePosition({ x, y });

      // Handle reference aircraft dragging
      if (isReferenceAircraftDragging) {
        if (lastMousePos.current) {
          const dx = x - lastMousePos.current.x;
          const dy = y - lastMousePos.current.y;
          // Convert delta from pixels to feet
          const dxFeet = dx / pixelsToFeetRatio;
          const dyFeet = dy / pixelsToFeetRatio;
          setReferencePosition({
            x: referencePosition.x + dxFeet,
            y: referencePosition.y + dyFeet,
            angle: referencePosition.angle,
          });
        }
        lastMousePos.current = { x, y };
        return;
      } else {
        lastMousePos.current = null;
      }

      if (draggingPoint) {
        const { shapeId, pointIndex } = draggingPoint;
        if (shapeId) {
          const updatedShape = shapes.find((s) => s.id === shapeId);
          if (
            updatedShape.type === "Tie Down T" ||
            updatedShape.type === "Double T" ||
            updatedShape.type === "Circle" ||
            updatedShape.type === "Helipad" ||
            updatedShape?.type === "Compass Rose"
          ) {
            // Fixed adjustment logic for square
            const center = getCenter(updatedShape.points);
            const adjustedPoints = adjustSquare(
              updatedShape.points,
              pointIndex,
              { x, y },
              center
            );
            // if shift is pressed, snap to 45 degree angles
            if (isShiftPressed) {
              //  TODO: implement here
              updatedShape.points = snapSquare(adjustedPoints);
            } else {
              updatedShape.points = adjustedPoints;
            }
          } else {
            updatedShape.points[pointIndex] = { x, y };
          }
          setShapes(updateAtId(shapes, updatedShape));
        } else if (draggingShape) {
          // Update point in an existing shape
          let updatedShapes = [...shapes];
          const draggingShape = shapes.find((s) => s.id === shapeId);
          if (
            draggingShape?.type === "Tie Down T" ||
            draggingShape?.type === "Double T" ||
            draggingShape?.type === "Circle" ||
            draggingShape?.type === "Helipad" ||
            draggingShape?.type === "Compass Rose"
          ) {
            // Fixed adjustment logic for square
            const center = getCenter(draggingShape.points);
            const adjustedPoints = adjustSquare(
              draggingShape.points,
              pointIndex,
              { x, y },
              center
            );
            // if shift is pressed, snap to 45 degree angles
            if (isShiftPressed) {
              //  TODO: implement here
              draggingShape.points = snapSquare(adjustedPoints);
            } else {
              draggingShape.points = adjustedPoints;
            }
          } else {
            draggingShape.points[pointIndex] = { x, y };
          }
          setShapes(updateAtId(updatedShapes, draggingShape));
        }
      } else if (draggingShape) {
        const { shapeId, offset } = draggingShape;
        const dx = x - offset.x;
        const dy = y - offset.y;

        let updatedShapes = [...shapes];
        getActiveShapes().forEach((shape) => {
          updatedShapes = updateAtId(updatedShapes, {
            ...shape,
            points: shape.points.map((point) => ({
              x: point.x + dx,
              y: point.y + dy,
            })),
          });
        });
        setShapes(updatedShapes);
        setDraggingShape({ shapeId, offset: { x, y } });
      }
    },
    [
      props,
      isReferenceAircraftDragging,
      referencePosition,
      setReferencePosition,
      pixelsToFeetRatio,
    ]
  );
};
